import React, { useState } from 'react';
import { IconButton, Typography, Box, Fade, Tooltip, TextField, Chip } from '@mui/material';
import { EditOutlined, SaveOutlined, CancelOutlined } from '@mui/icons-material';
import PeopleIcon from '@mui/icons-material/People';
import { useNavigate } from 'react-router-dom';

const NameDescriptionSection = ({
  isEditing,
  name,
  setName,
  description,
  setDescription,
  handleCancelClick,
  handleSaveClick,
  handleEditClick,
  StyledTextField,
  disabled,
  hideTitle = false, // New prop to hide the title
  creatorUserName = null, // Username of the space creator
  subscriberCount = null, // Subscriber count to display
  isPublic = false, // Whether the space is public
  displayFollowersCount = true // Whether to display followers count
}) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Constants
  const MAX_DESCRIPTION_LENGTH = 150;
  const isLongDescription = description && description.length > MAX_DESCRIPTION_LENGTH;
  const truncatedText = isLongDescription
    ? `${description.slice(0, MAX_DESCRIPTION_LENGTH)}...`
    : description || '';

  // Styles
  const styles = {
    container: {
      marginBottom: '0px',
      position: 'relative',
      transition: 'all 0.3s ease',
    },
    nameContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: hideTitle ? 'flex-start' : 'center',
      marginBottom: hideTitle ? '0px' : '12px',
      position: 'relative',
    },
    nameText: {
      fontWeight: 700,
      color: '#1976d2',
      marginBottom: '8px',
      marginTop: 0,
      transition: 'all 0.3s ease',
      textAlign: hideTitle ? 'left' : 'center',
      fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' },
      lineHeight: 1.2,
      '&:hover': {
        color: '#1565c0',
      },
    },
    creatorText: {
      fontSize: '0.875rem',
      color: 'text.secondary',
      marginTop: '4px',
      marginBottom: '12px',
      textAlign: hideTitle ? 'left' : 'center',
      transition: 'all 0.2s ease',
    },
    creatorLink: {
      color: '#1976d2',
      cursor: 'pointer',
      textDecoration: 'none',
      fontWeight: 500,
      '&:hover': {
        color: '#1565c0',
        textDecoration: 'underline',
      },
    },
    descriptionContainer: {
      marginTop: hideTitle ? '0px' : '8px',
      position: 'relative',
      transition: 'all 0.3s ease',
    },
    descriptionText: {
      color: 'text.primary',
      lineHeight: 1.6,
      fontSize: { xs: '0.875rem', sm: '1rem' },
      transition: 'all 0.3s ease',
      fontWeight: 400,
      maxWidth: '100%',
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
      textAlign: 'left',
      overflow: 'hidden',
    },
    showMoreButton: {
      marginTop: { xs: '8px', sm: '12px' },
      padding: { xs: '6px 12px', sm: '8px 16px' },
      border: 'none',
      background: 'linear-gradient(45deg, #1976d2, #1565c0)',
      color: 'white',
      cursor: 'pointer',
      fontWeight: 600,
      borderRadius: '8px',
      fontSize: { xs: '0.8rem', sm: '0.875rem' },
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)',
      display: 'inline-block',
      '&:hover': {
        background: 'linear-gradient(45deg, #1565c0, #0d47a1)',
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
      },
    },
    nameField: {
      mb: { xs: 3, sm: 5 },
      '& .MuiOutlinedInput-input': {
        fontSize: { xs: '1.2rem', sm: '1.5rem' },
        fontWeight: 600,
        padding: { xs: '10px 12px', sm: '14px 16px' },
        textAlign: hideTitle ? 'left' : 'center',
      },
      '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: 'rgba(25, 118, 210, 0.02)',
        '&:hover fieldset': {
          borderColor: '#1976d2',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#1976d2',
          borderWidth: '2px',
        },
      },
      '& .MuiInputLabel-root': {
        fontWeight: 600,
        '&.Mui-focused': {
          color: '#1976d2',
        },
      },
    },
    descriptionField: {
      '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: 'rgba(25, 118, 210, 0.02)',
        '&:hover fieldset': {
          borderColor: '#1976d2',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#1976d2',
          borderWidth: '2px',
        },
      },
      '& .MuiInputLabel-root': {
        fontWeight: 600,
        '&.Mui-focused': {
          color: '#1976d2',
        },
      },
      '& .MuiFormHelperText-root': {
        textAlign: 'right',
        fontWeight: 500,
        fontSize: '0.75rem',
        marginTop: '8px',
      },
      '& .MuiOutlinedInput-input': {
        textAlign: 'left',
        lineHeight: 1.6,
      },
    },
  };

  return (
    <Box 
      sx={styles.container}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Name Section - Always show when editing, otherwise respect hideTitle */}
      {(isEditing || !hideTitle) && (
        <Box sx={styles.nameContainer}>
          {isEditing ? (
            <StyledTextField
              disabled={disabled}
              value={name}
              onChange={(e) => setName(e.target.value)}
              label="Collection Name"
              variant="outlined"
              fullWidth
              sx={styles.nameField}
              autoFocus
            />
          ) : (
            <>
              <Typography
                variant="h5"
                sx={styles.nameText}
              >
                {name}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: hideTitle ? 'flex-start' : 'center', gap: 0.5 }}>
                {creatorUserName && (
                  <Typography
                    variant="body2"
                    sx={styles.creatorText}
                  >
                    Created by{' '}
                    <Box
                      component="span"
                      sx={styles.creatorLink}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate(`/user/${creatorUserName}`);
                      }}
                    >
                      {creatorUserName}
                    </Box>
                  </Typography>
                )}
                {/* Subscriber count - only show for public spaces if display_followers_count is true */}
                {!isEditing && isPublic && displayFollowersCount !== false && subscriberCount !== null && subscriberCount !== undefined && (
                  <Chip
                    icon={<PeopleIcon sx={{ fontSize: '16px !important' }} />}
                    label={`${subscriberCount} ${subscriberCount === 1 ? 'follower' : 'followers'}`}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      color: '#1976d2',
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      height: '24px',
                      '& .MuiChip-icon': {
                        color: '#1976d2',
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.12)',
                      },
                    }}
                  />
                )}
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Spacer between name and description when editing */}
      {isEditing && (
        <Box sx={{ height: '32px' }} />
      )}

      {/* Description Section */}
      {(description || isEditing) && (
        <>
          {!hideTitle && !isEditing && (
            <Typography 
              variant="h6" 
              sx={{ 
                fontSize: '1.1rem', 
                fontWeight: 600, 
                color: 'text.primary',
                mb: 1.5,
                textAlign: 'left'
              }}
            >
              About this space
            </Typography>
          )}
          {isEditing ? (
            <StyledTextField
              disabled={disabled}
              value={description || ''}
              onChange={(e) => {
                const newValue = e.target.value.slice(0, 5000); // INPUT_LIMITS.SPACE_DESCRIPTION
                setDescription(newValue);
              }}
              label="Description"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              sx={styles.descriptionField}
              helperText={`${description?.length || 0}/5000 characters`}
              inputProps={{
                maxLength: 5000 // INPUT_LIMITS.SPACE_DESCRIPTION
              }}
            />
          ) : (
            <Box sx={styles.descriptionContainer}>
              <Typography 
                variant="body1" 
                sx={styles.descriptionText}
              >
                {(!isLongDescription || isExpanded)
                  ? description
                  : truncatedText}
              </Typography>
              {isLongDescription && (
                <Box
                  component="button"
                  onClick={() => setIsExpanded(!isExpanded)}
                  sx={styles.showMoreButton}
                >
                  {isExpanded ? 'Show less' : 'Show more'}
                </Box>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default NameDescriptionSection;
